import { useRouter } from "@/lib/navigation";
import { ArrowLeft, TrendingUp, Wallet, CreditCard, AlertCircle, Wrench, Building2, BedDouble, IndianRupee, Pencil } from "lucide-react";
import Badge from "../../components/Badge";
import InlineStatusBadge from "../../components/InlineStatusBadge";
import { EventDetail } from "./types";

interface FinancialSummary {
  total: number;
  paid: number;
  balance: number;
  profit: number;
}

// Must match the statuses the events API accepts
const EVENT_STATUSES = ["draft", "confirmed", "in_progress", "completed", "cancelled"];

interface EventHeaderProps {
  event: EventDetail;
  financials?: FinancialSummary;
  showProfit?: boolean;
  onEdit?: () => void;
  onStatusChange?: (status: string) => Promise<void> | void;
  /** Statuses this event can move to next (from the server); defaults to all */
  statusOptions?: string[];
  onAddService?: () => void;
  onAddVendor?: () => void;
  onAddRoom?: () => void;
  onAddPayment?: () => void;
}

function fmt(n: number) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

const titleCase = (s?: string | null) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

export default function EventHeader({
  event,
  financials,
  showProfit = true,
  onEdit,
  onStatusChange,
  statusOptions,
  onAddService,
  onAddVendor,
  onAddRoom,
  onAddPayment,
}: EventHeaderProps) {
  const router = useRouter();

  const hasFinancials = financials && financials.total > 0;
  const isPaid = hasFinancials && financials.balance <= 0;
  const paidPct = hasFinancials ? Math.min(100, Math.round((financials.paid / financials.total) * 100)) : 0;

  const eventDate = event.start_date
    ? new Date(event.start_date.split("T")[0]).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  const meta = [
    `#${event.id}`,
    event.client?.name,
    event.event_type && titleCase(event.event_type),
    eventDate,
    event.venue?.name,
  ].filter(Boolean) as string[];

  const quickActions = [
    onAddPayment && { label: "Payment", icon: IndianRupee, onClick: onAddPayment, cls: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200" },
    onAddService && { label: "Service", icon: Wrench, onClick: onAddService, cls: "bg-violet-50 text-violet-700 hover:bg-violet-100 border-violet-200" },
    onAddVendor && { label: "Vendor", icon: Building2, onClick: onAddVendor, cls: "bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200" },
    onAddRoom && { label: "Room", icon: BedDouble, onClick: onAddRoom, cls: "bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200" },
  ].filter(Boolean) as { label: string; icon: typeof Wrench; onClick: () => void; cls: string }[];

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
      {/* Top bar */}
      <div className="flex items-start justify-between gap-3 px-3 sm:px-4 py-3 bg-gradient-to-r from-stone-900 to-stone-700 rounded-t-2xl last:rounded-b-2xl">
        <div className="flex items-start gap-3 min-w-0">
          <button
            onClick={() => router.push("/events")}
            aria-label="Back to events"
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <h1 className="font-extrabold text-white text-base sm:text-lg leading-tight break-words">
              {event.name}
            </h1>
            <p className="text-xs text-stone-300 mt-1 leading-relaxed">
              {meta.join(" · ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onStatusChange ? (
            <InlineStatusBadge
              status={event.status}
              options={statusOptions ? [event.status, ...statusOptions.filter(s => s !== event.status)] : EVENT_STATUSES}
              onChange={onStatusChange}
            />
          ) : (
            <Badge label={event.status.replace(/_/g, " ")} status={event.status} />
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              aria-label="Edit event"
              className="h-10 min-w-10 px-2 sm:px-3 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors"
            >
              <Pencil className="w-4 h-4" />
              <span className="hidden sm:inline">Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Financial summary bar */}
      {hasFinancials && (
        <div className="px-4 py-3 bg-stone-50 border-b border-stone-100">
          <div className={`grid grid-cols-2 gap-3 ${showProfit ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-3.5 h-3.5 text-stone-500" />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Event Total</p>
                <p className="text-sm font-bold text-stone-800">{fmt(financials.total)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Received</p>
                <p className="text-sm font-bold text-emerald-700">{fmt(financials.paid)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isPaid ? "bg-emerald-50" : "bg-amber-50"}`}>
                <AlertCircle className={`w-3.5 h-3.5 ${isPaid ? "text-emerald-600" : "text-amber-600"}`} />
              </div>
              <div>
                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Balance</p>
                <p className={`text-sm font-bold ${isPaid ? "text-emerald-700" : "text-amber-700"}`}>
                  {isPaid ? "Cleared" : fmt(financials.balance)}
                </p>
              </div>
            </div>

            {showProfit && (
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${financials.profit >= 0 ? "bg-blue-50" : "bg-rose-50"}`}>
                  <TrendingUp className={`w-3.5 h-3.5 ${financials.profit >= 0 ? "text-blue-600" : "text-rose-600"}`} />
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">Profit</p>
                  <p className={`text-sm font-bold ${financials.profit >= 0 ? "text-blue-700" : "text-rose-700"}`}>
                    {fmt(financials.profit)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Payment progress bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-stone-400 font-medium">Payment progress</span>
              <span className="text-[10px] font-bold text-stone-600">{paidPct}%</span>
            </div>
            <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${isPaid ? "bg-emerald-500" : "bg-amber-500"}`}
                style={{ width: `${paidPct}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick action strip */}
      {quickActions.length > 0 && (
        <div className="px-3 sm:px-4 py-2 flex flex-wrap items-center gap-2 bg-white rounded-b-2xl">
          <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mr-1">Add</span>
          {quickActions.map(({ label, icon: Icon, onClick, cls }) => (
            <button
              key={label}
              onClick={onClick}
              className={`flex items-center gap-1.5 text-xs font-semibold min-h-10 md:min-h-8 px-3 py-1.5 rounded-lg border transition-colors ${cls}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
